// ReportsPanel — fee reports: Collection (payments received in a date range)
// and Dues (who owes what, with one-tap reminders). Both export to CSV and
// print. Attendance reports stay in their own AttendanceReportsPanel.
import { useEffect, useState } from 'react';
import { Download, Printer, Bell } from 'lucide-react';
import { getFeeCollectionReport, getDuesReport, getClasses, sendFeeReminder, applyLateFine } from '../api/client';
import { EnquiryReport, FollowupReport, DirectorReport, UdiseReport } from './MoreReports';
import Button from '../ui/Button';
import { Card, CardHeader, StatTile } from '../ui/Card';
import PrintSheet, { PrintHeader } from '../ui/PrintSheet';
import { inr, fmtDate, fmtMonth, todayISO, downloadCsv } from '../ui/format';

const inputCls = 'border border-line rounded-s px-3 py-2 text-sm bg-paper-raised text-ink focus:outline-none focus:ring-2 focus:ring-transit';
const thCls = 'px-4 py-2.5';

export default function ReportsPanel({ initialTab = 'collection', onOpenStudent, schoolName }) {
  const [tab,     setTab]     = useState(initialTab);
  const [classes, setClasses] = useState([]);

  useEffect(() => { setTab(initialTab); }, [initialTab]);
  useEffect(() => { getClasses().then(setClasses).catch(() => {}); }, []);

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">Reports</h2>
      <div className="flex flex-wrap gap-1.5 mb-5 bg-transit-tint p-1 rounded-m w-fit">
        {[['collection', 'Fee collection'], ['dues', 'Dues'], ['director', 'Director summary'], ['enquiries', 'Enquiries'],
          ['followups', 'Follow-ups'], ['udise', 'UDISE enrolment']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-s text-[13px] font-bold transition-colors ${tab === k ? 'bg-transit text-white shadow-1' : 'text-transit-deep'}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'collection' && <CollectionReport classes={classes} onOpenStudent={onOpenStudent} schoolName={schoolName} />}
      {tab === 'dues'       && <DuesReport classes={classes} onOpenStudent={onOpenStudent} schoolName={schoolName} />}
      {tab === 'director'   && <DirectorReport schoolName={schoolName} />}
      {tab === 'enquiries'  && <EnquiryReport />}
      {tab === 'followups'  && <FollowupReport />}
      {tab === 'udise'      && <UdiseReport schoolName={schoolName} />}
    </div>
  );
}

function ClassSelect({ classes, value, onChange }) {
  return (
    <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Class
      <select className={inputCls} value={value} onChange={e => onChange(e.target.value)}>
        <option value="">All classes</option>
        {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    </label>
  );
}

// ── Collection ───────────────────────────────────────────────
function CollectionReport({ classes, onOpenStudent, schoolName }) {
  const [from,    setFrom]    = useState(() => `${todayISO().slice(0, 7)}-01`);
  const [to,      setTo]      = useState(todayISO);
  const [classId, setClassId] = useState('');
  const [data,    setData]    = useState(null);
  const [msg,     setMsg]     = useState('');
  const [print,   setPrint]   = useState(false);

  useEffect(() => { load(); }, [from, to, classId]);

  async function load() {
    if (!from || !to) return;
    setMsg('');
    try { setData(await getFeeCollectionReport({ from, to, class_id: classId || undefined })); }
    catch (err) { setMsg('Failed to load report: ' + (err.response?.data?.error || err.message)); }
  }

  function exportCsv() {
    downloadCsv(`fee-collection_${from}_to_${to}.csv`, [
      { key: 'receipt_no', label: 'Receipt No' }, { key: 'paid_on', label: 'Paid On' },
      { key: 'student_name', label: 'Student' }, { key: 'class_name', label: 'Class' },
      { key: 'roll_number', label: 'Roll' }, { key: 'admission_no', label: 'Admission No' },
      { key: 'fee_month', label: 'Fee Month' }, { key: 'note', label: 'Note' }, { key: 'payment_mode', label: 'Mode' },
      { key: 'payment_ref', label: 'Reference' }, { key: 'amount', label: 'Amount' },
    ], data.rows.map(r => ({ ...r, paid_on: fmtDate(r.paid_at) })));
  }

  return (
    <>
      <Card className="p-4 mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">From
          <input type="date" className={inputCls} value={from} max={to} onChange={e => setFrom(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">To
          <input type="date" className={inputCls} value={to} min={from} onChange={e => setTo(e.target.value)} />
        </label>
        <ClassSelect classes={classes} value={classId} onChange={setClassId} />
        <div className="flex gap-2 ml-auto">
          <Button variant="secondary" disabled={!data?.rows.length} onClick={exportCsv} className="flex items-center gap-1.5"><Download size={14} /> CSV</Button>
          <Button variant="secondary" disabled={!data?.rows.length} onClick={() => setPrint(true)} className="flex items-center gap-1.5"><Printer size={14} /> Print</Button>
        </div>
      </Card>

      {msg && <p className="text-critical text-sm font-bold mb-3">{msg}</p>}

      {data && (
        <>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3.5 mb-4">
            <StatTile tone="success" label="Collected" value={inr(data.total_collected)} caption={`${fmtDate(data.from)} – ${fmtDate(data.to)}`} />
            <StatTile tone="transit" label="Payments" value={data.payments} />
            {data.refunds > 0 && <StatTile tone="critical" label="Refunds" value={inr(data.refunds)} caption={`Net ${inr(data.net_collected)}`} />}
          </div>
          {data.by_mode && Object.keys(data.by_mode).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.entries(data.by_mode).map(([mode, amt]) => (
                <span key={mode} className="text-sm bg-paper-raised border border-line rounded-full px-3 py-1"><b>{mode}</b> {inr(amt)}</span>
              ))}
            </div>
          )}
          <Card>
            <CardHeader title="Payments received" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                    <th className={thCls}>Receipt</th><th className={thCls}>Paid on</th><th className={thCls}>Student</th>
                    <th className={thCls}>Class</th><th className={thCls}>Fee month</th><th className={thCls}>Mode</th><th className={`${thCls} text-right`}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(r => (
                    <tr key={r.receipt_no} className="border-t border-line-soft hover:bg-transit-tint/40">
                      <td className="px-4 py-3 text-ink-soft tabular-nums">#{r.receipt_no}</td>
                      <td className="px-4 py-3">{fmtDate(r.paid_at)}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => onOpenStudent(r.student_id, 'fees')} className="font-semibold text-transit-deep hover:underline">{r.student_name}</button>
                      </td>
                      <td className="px-4 py-3">{r.class_name || '—'}</td>
                      <td className="px-4 py-3">{r.fee_month}{r.note ? <span className="text-ink-soft"> · {r.note}</span> : null}</td>
                      <td className="px-4 py-3">{r.payment_mode || '—'}{r.payment_ref ? <span className="block text-xs text-ink-soft">{r.payment_ref}</span> : null}</td>
                      <td className="px-4 py-3 text-right font-bold tabular-nums">{inr(r.amount)}</td>
                    </tr>
                  ))}
                  {!data.rows.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-ink-soft">No payments in this period</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {print && data && (
        <PrintSheet title="Fee collection report" onClose={() => setPrint(false)}>
          <PrintHeader schoolName={schoolName} docTitle="Fee Collection Report"
            right={<>{fmtDate(data.from)} – {fmtDate(data.to)}{classId ? <><br />Class {classes.find(c => String(c.id) === classId)?.name}</> : null}</>} />
          <table className="w-full text-sm border-collapse">
            <thead><tr><th className="text-left">Receipt</th><th className="text-left">Date</th><th className="text-left">Student</th><th className="text-left">Class</th><th className="text-left">Fee month</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.receipt_no}><td>#{r.receipt_no}</td><td>{fmtDate(r.paid_at)}</td><td>{r.student_name}</td><td>{r.class_name || ''}</td><td>{r.fee_month}</td><td className="text-right">{inr(r.amount)}</td></tr>
              ))}
              <tr><td colSpan={5} className="font-bold">Total ({data.payments} payments)</td><td className="text-right font-bold">{inr(data.total_collected)}</td></tr>
            </tbody>
          </table>
        </PrintSheet>
      )}
    </>
  );
}

// ── Dues ─────────────────────────────────────────────────────
function DuesReport({ classes, onOpenStudent, schoolName }) {
  const [classId, setClassId] = useState('');
  const [data,    setData]    = useState(null);
  const [msg,     setMsg]     = useState('');
  const [print,   setPrint]   = useState(false);
  const [sending, setSending] = useState(null); // student_id | 'all' | null
  const [sent,    setSent]    = useState({});   // student_id -> true

  useEffect(() => { load(); }, [classId]);

  async function load() {
    setMsg('');
    try { setData(await getDuesReport({ class_id: classId || undefined })); }
    catch (err) { setMsg('Failed to load report: ' + (err.response?.data?.error || err.message)); }
  }

  async function lateFine() {
    const input = window.prompt('Late fine per overdue month (₹)?\nIt is added once to every unpaid month whose due date has passed' +
      (classId ? ' in this class' : '') + '. Months already fined are skipped.', '50');
    if (input === null) return;
    const amount = Number(input);
    if (!(amount > 0)) return setMsg('Error: enter a positive amount');
    try {
      const r = await applyLateFine({ amount, class_id: classId ? Number(classId) : undefined });
      await load(); // refresh first — load() clears the message line
      setMsg(r.months_fined
        ? `Late fine of ${inr(amount)} added to ${r.months_fined} month${r.months_fined === 1 ? '' : 's'} (${r.students} student${r.students === 1 ? '' : 's'}) ✅`
        : 'No months to fine — nothing is past its due date, or those months were already fined.');
    } catch (err) { setMsg('Error: ' + (err.response?.data?.error || err.message)); }
  }

  async function remind(ids) {
    setSending(ids.length === 1 ? ids[0] : 'all');
    let failed = 0;
    for (const id of ids) {
      try { await sendFeeReminder(id); setSent(s => ({ ...s, [id]: true })); }
      catch { failed += 1; }
    }
    setSending(null);
    setMsg(failed ? `Error: ${failed} reminder(s) failed to send` : `Reminder sent to ${ids.length} guardian${ids.length > 1 ? 's' : ''} ✅`);
  }

  function exportCsv() {
    downloadCsv('fee-dues.csv', [
      { key: 'student_name', label: 'Student' }, { key: 'class_name', label: 'Class' },
      { key: 'roll_number', label: 'Roll' }, { key: 'admission_no', label: 'Admission No' },
      { key: 'guardian_name', label: 'Guardian' }, { key: 'guardian_phone', label: 'Phone' },
      { key: 'pending_months', label: 'Months Due' }, { key: 'since', label: 'Due Since' }, { key: 'amount_due', label: 'Amount Due' },
    ], data.rows.map(r => ({ ...r, since: fmtMonth(r.pending_since) })));
  }

  return (
    <>
      <Card className="p-4 mb-4 flex flex-wrap items-end gap-3">
        <ClassSelect classes={classes} value={classId} onChange={setClassId} />
        <div className="flex gap-2 ml-auto flex-wrap">
          <Button disabled={!data?.rows.length || sending != null}
            onClick={() => window.confirm(`Send a fee reminder to all ${data.rows.length} guardians on this list?`) && remind(data.rows.map(r => r.student_id))}
            className="flex items-center gap-1.5">
            <Bell size={14} /> {sending === 'all' ? 'Sending…' : 'Remind all'}
          </Button>
          <Button variant="secondary" disabled={!data?.rows.length} onClick={lateFine}>Apply late fine</Button>
          <Button variant="secondary" disabled={!data?.rows.length} onClick={exportCsv} className="flex items-center gap-1.5"><Download size={14} /> CSV</Button>
          <Button variant="secondary" disabled={!data?.rows.length} onClick={() => setPrint(true)} className="flex items-center gap-1.5"><Printer size={14} /> Print</Button>
        </div>
      </Card>

      {msg && (
        <p className={`text-sm font-bold px-3 py-2 rounded-s w-fit mb-3 ${msg.startsWith('Error') || msg.startsWith('Failed') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>{msg}</p>
      )}

      {data && (
        <>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3.5 mb-4">
            <StatTile tone="caution" label="Total due" value={inr(data.total_due)} />
            <StatTile tone="critical" label="Students with dues" value={data.students} />
          </div>
          <Card>
            <CardHeader title="Outstanding fees" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                    <th className={thCls}>Student</th><th className={thCls}>Class</th><th className={thCls}>Guardian</th>
                    <th className={thCls}>Months due</th><th className={thCls}>Since</th>
                    <th className={`${thCls} text-right`}>Amount due</th><th className={thCls}>Reminder</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(r => (
                    <tr key={r.student_id} className="border-t border-line-soft hover:bg-transit-tint/40">
                      <td className="px-4 py-3">
                        <button onClick={() => onOpenStudent(r.student_id, 'fees')} className="font-semibold text-transit-deep hover:underline">{r.student_name}</button>
                      </td>
                      <td className="px-4 py-3">{r.class_name || '—'}{r.roll_number ? ` · #${r.roll_number}` : ''}</td>
                      <td className="px-4 py-3">{r.guardian_name || '—'}{r.guardian_phone && <span className="block text-xs text-ink-soft">{r.guardian_phone}</span>}</td>
                      <td className="px-4 py-3 tabular-nums">{r.pending_months}</td>
                      <td className="px-4 py-3">{fmtMonth(r.pending_since)}</td>
                      <td className="px-4 py-3 text-right font-bold text-critical tabular-nums">{inr(r.amount_due)}</td>
                      <td className="px-4 py-3">
                        {sent[r.student_id]
                          ? <span className="text-xs font-bold text-success">Sent ✓</span>
                          : <Button variant="secondary" className="!px-2.5 !py-1 !text-xs" disabled={sending != null}
                              onClick={() => remind([r.student_id])}>{sending === r.student_id ? 'Sending…' : 'Remind'}</Button>}
                        {r.reminder_sent_at && !sent[r.student_id] && <span className="block text-[11px] text-ink-soft mt-1">Last {fmtDate(r.reminder_sent_at)}</span>}
                      </td>
                    </tr>
                  ))}
                  {!data.rows.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-ink-soft">No outstanding fees 🎉</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {print && data && (
        <PrintSheet title="Dues report" onClose={() => setPrint(false)}>
          <PrintHeader schoolName={schoolName} docTitle="Fee Dues Report"
            right={<>As of {fmtDate(todayISO())}{classId ? <><br />Class {classes.find(c => String(c.id) === classId)?.name}</> : null}</>} />
          <table className="w-full text-sm border-collapse">
            <thead><tr><th className="text-left">Student</th><th className="text-left">Class</th><th className="text-left">Guardian</th><th className="text-left">Phone</th><th className="text-left">Months</th><th className="text-right">Amount due</th></tr></thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.student_id}><td>{r.student_name}</td><td>{r.class_name || ''}</td><td>{r.guardian_name || ''}</td><td>{r.guardian_phone || ''}</td><td>{r.pending_months}</td><td className="text-right">{inr(r.amount_due)}</td></tr>
              ))}
              <tr><td colSpan={5} className="font-bold">Total ({data.students} students)</td><td className="text-right font-bold">{inr(data.total_due)}</td></tr>
            </tbody>
          </table>
        </PrintSheet>
      )}
    </>
  );
}
